import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, SpinnerGap, Broom, Check } from "@phosphor-icons/react";
import { api, errMsg, img } from "@/lib/api";
import { FullLoader } from "@/lib/auth";
import { LogoMark } from "@/components/common";
import { HkPhotos } from "@/components/guest/HkPhotos";

export default function Housekeeper() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState(localStorage.getItem("hk_name") || "");
  const [checked, setChecked] = useState([]);
  const [comments, setComments] = useState("");
  const [issues, setIssues] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [photos, setPhotos] = useState([]);

  useEffect(() => { api.get(`/public/h/${code}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e, "Checklist not found"))); }, [code]);

  const toggle = (i) => setChecked((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]));
  const submit = async () => {
    setErr("");
    if (name.trim().length < 2) return setErr("Please enter your name.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("data", JSON.stringify({ name, checked, comments, issues }));
      photos.forEach((p) => fd.append("photos", p.file));
      const r = await api.post(`/public/h/${code}`, fd);
      localStorage.setItem("hk_name", name);
      setDone(r.data);
      setPhotos([]);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  if (!d && err) return <div className="grid min-h-screen place-items-center px-6 text-center" data-testid="hk-not-found"><p className="font-display text-2xl">{err}</p></div>;
  if (!d) return <FullLoader />;

  return (
    <div className="min-h-screen bg-[#F3F6F4]" data-testid="housekeeper-page">
      <div className="mx-auto max-w-md pb-10">
        <header className="relative h-40 overflow-hidden bg-[#2F6B4F]">
          {d.cover && <img src={img(d.cover)} alt="" className="h-full w-full object-cover opacity-40" />}
          <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
            <p className="label-xs flex items-center gap-1.5 opacity-90"><Broom size={14} /> Housekeeping</p>
            <h1 className="mt-1 font-display text-2xl font-medium">{d.name}</h1>
          </div>
        </header>
        <div className="space-y-4 p-4">
          {done ? (
            <div className="rise rounded-3xl bg-white p-6 text-center" data-testid="hk-done">
              <CheckCircle size={46} weight="fill" className="mx-auto text-[#2F6B4F]" />
              <p className="mt-3 font-display text-2xl">Thank you, {name.split(" ")[0]}!</p>
              <p className="mt-1 text-sm text-muted-foreground">{done.done} of {done.total} tasks ticked. {d.host_name || "The host"} has been notified.</p>
              <button onClick={() => { setDone(null); setChecked([]); setComments(""); setIssues(false); }} data-testid="hk-new-report" className="mt-5 h-11 rounded-full border border-border px-5 text-sm font-semibold">Start a new checklist</button>
            </div>
          ) : (
            <>
              <label className="block rounded-3xl bg-white p-5">
                <span className="text-sm font-medium">Your name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="hk-name-input" className="mt-1.5 h-12 w-full rounded-xl border border-border bg-[#F3F6F4] px-3.5 text-base outline-none focus:ring-2 focus:ring-[#2F6B4F]" />
              </label>
              <section className="rounded-3xl bg-white p-5">
                <div className="flex items-center justify-between"><p className="font-display text-lg">Checklist</p><span className="text-sm font-semibold text-[#2F6B4F]" data-testid="hk-progress">{checked.length}/{d.items.length}</span></div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#E4ECE7]"><div className="h-full rounded-full bg-[#2F6B4F] transition-[width] duration-300" style={{ width: `${d.items.length ? (100 * checked.length) / d.items.length : 0}%` }} /></div>
                <div className="mt-4 space-y-2">
                  {d.items.map((it, i) => {
                    const on = checked.includes(it);
                    return (
                      <button key={it} type="button" onClick={() => toggle(it)} data-testid={`hk-item-${i}`} className={`flex w-full items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left text-[15px] transition-colors duration-200 ${on ? "border-[#2F6B4F] bg-[#E9F2EC]" : "border-border bg-white"}`}>
                        <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 ${on ? "border-[#2F6B4F] bg-[#2F6B4F] text-white" : "border-[#C9D3CD]"}`}>{on && <Check size={14} weight="bold" />}</span>
                        <span className={on ? "text-[#1E3D2D]" : ""}>{it}</span>
                      </button>
                    );
                  })}
                </div>
                {d.items.length > 0 && <button type="button" onClick={() => setChecked(checked.length === d.items.length ? [] : [...d.items])} data-testid="hk-toggle-all" className="mt-3 text-sm font-semibold text-[#2F6B4F]">{checked.length === d.items.length ? "Untick all" : "Tick all"}</button>}
              </section>
              <section className="rounded-3xl bg-white p-5">
                <p className="font-display text-lg">Comments</p>
                <textarea rows={4} value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Anything broken, missing or needing restock?" data-testid="hk-comments-input" className="mt-2 w-full rounded-xl border border-border bg-[#F3F6F4] p-3.5 text-base outline-none focus:ring-2 focus:ring-[#2F6B4F]" />
                <label className="mt-3 flex items-center gap-3 text-sm"><input type="checkbox" checked={issues} onChange={(e) => setIssues(e.target.checked)} data-testid="hk-issue-checkbox" className="h-5 w-5 accent-[#A6431F]" /> Something needs the host's attention</label>
              </section>
              <HkPhotos photos={photos} setPhotos={setPhotos} />
              {err && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="hk-error">{err}</p>}
              <button onClick={submit} disabled={busy} data-testid="hk-submit-button" className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#2F6B4F] font-semibold text-white disabled:opacity-60">{busy ? <SpinnerGap className="animate-spin" /> : "Submit checklist"}</button>
            </>
          )}
          <p className="flex items-center justify-center gap-1.5 pt-2 text-xs text-muted-foreground"><LogoMark size={14} /> <b>hostkey.online</b></p>
        </div>
      </div>
    </div>
  );
}
